import { useState } from "react";
import { motion } from "framer-motion";
import { Lightning, Compass, Check, ArrowRight, LockSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { api, formatApiError, scrollToId } from "../lib/api";

const TIERS = [
  {
    id: "clarity",
    icon: Lightning,
    name: "The Clarity Session",
    price: "$499",
    cadence: "one time",
    tagline: "A quick fact, when a quick fact is all you need.",
    description:
      "Send us your scenario/proposal in writing — the offer, the company, the fees. You'll receive a considered, personal written answer, one-on-one.",
    features: [
      "One written one-on-one consultation answered personally",
      "Any contract, offer or company reviewed in detail",
      "Written summary of your options within 24 hours",
      "One follow-up exchange, answered personally",
    ],
    cta: "Book my Clarity Session",
    lookupKey: "clarity_session",
    planNote: "I'm interested in the Clarity Session (one-time consultation).",
    featured: false,
  },
  {
    id: "compass",
    icon: Compass,
    name: "The Compass Membership",
    price: "$249",
    cadence: "per month",
    tagline: "A hand to hold, until the process is complete",
    description:
      "Some situations don't end in a single session — a negotiation, a sale, a full exit. Membership means we stay beside you from the first question to the final signature.",
    features: [
      "Unlimited written consultations — a call only if necessary",
      "Every offer and contract reviewed before you sign anything",
      "We stay engaged through your negotiation, sale or exit — start to finish",
      "Priority replies within one business day, always",
      "Cancel anytime. No minimum term, no exit interview",
    ],
    cta: "Start my membership",
    lookupKey: "compass_monthly",
    planNote: "I'm interested in the Compass Membership (ongoing consultations).",
    featured: true,
  },
];

export default function Pricing() {
  const [loadingTier, setLoadingTier] = useState(null);

  const checkout = async (tier) => {
    setLoadingTier(tier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingTier(null);
    }
  };

  const enquire = (planNote) => {
    sessionStorage.setItem("preferred_plan", planNote);
    window.dispatchEvent(new CustomEvent("preferred-plan", { detail: planNote }));
    scrollToId("consult");
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-40 border-t border-border">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <Eyebrow>Two Ways Forward</Eyebrow>
          <h2 className="font-serif text-primary tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.08]" data-testid="pricing-heading">
            A quick fact — or a hand held <em className="text-accent">all the way</em>
          </h2>
          <p className="mt-6 text-foreground/70 max-w-xl">
            Every owner's purchase agreement ends the same way: with a decision made calmly, on real numbers.
            The only question is whether you want the detailed answer, or the promise.
          </p>
        </Reveal>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-2 gap-6 md:gap-8 max-w-5xl">
          {TIERS.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.12} className="h-full">
              <div
                data-testid={`pricing-card-${t.id}`}
                className={`relative h-full rounded-sm p-9 md:p-12 flex flex-col transition-[border-color,transform] duration-500 hover:-translate-y-1 ${
                  t.featured
                    ? "bg-primary text-primary-foreground border border-primary"
                    : "bg-card border border-border hover:border-accent/60"
                }`}
              >
                {t.featured && (
                  <span
                    data-testid="pricing-featured-badge"
                    className="absolute -top-3.5 left-9 bg-accent text-accent-foreground text-[11px] font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full"
                  >
                    Where most owners land
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <t.icon size={26} weight="duotone" className={t.featured ? "text-accent" : "text-accent"} />
                  <p className={`text-xs font-bold uppercase tracking-[0.25em] ${t.featured ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                    {t.name}
                  </p>
                </div>
                <div className="mt-8 flex items-baseline gap-3">
                  <span className="font-serif text-6xl md:text-7xl tracking-tight" data-testid={`pricing-price-${t.id}`}>{t.price}</span>
                  <span className={`text-sm font-semibold ${t.featured ? "text-primary-foreground/50" : "text-foreground/50"}`}>{t.cadence}</span>
                </div>
                <p className={`mt-5 font-serif italic text-xl leading-snug ${t.featured ? "text-accent" : "text-accent"}`}>
                  {t.tagline}
                </p>
                <p className={`mt-4 text-sm leading-relaxed ${t.featured ? "text-primary-foreground/70" : "text-foreground/65"}`}>
                  {t.description}
                </p>
                <ul className={`mt-8 space-y-3.5 flex-1 border-t pt-8 ${t.featured ? "border-primary-foreground/15" : "border-border"}`}>
                  {t.features.map((f, j) => (
                    <li key={j} className={`flex items-start gap-3 text-sm font-medium ${t.featured ? "text-primary-foreground/85" : "text-foreground/80"}`}>
                      <Check size={17} weight="bold" className="text-accent shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  data-testid={`pricing-cta-${t.id}`}
                  onClick={() => checkout(t)}
                  disabled={loadingTier !== null}
                  className={`group mt-10 flex items-center justify-center gap-3 px-8 py-4 rounded-full font-semibold text-sm transition-colors duration-300 disabled:opacity-60 ${
                    t.featured
                      ? "bg-accent text-accent-foreground hover:bg-background hover:text-primary"
                      : "bg-primary text-primary-foreground hover:bg-accent"
                  }`}
                >
                  {loadingTier === t.id ? "Opening secure checkout…" : t.cta}
                  <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </motion.button>
                <p className={`mt-4 flex items-center justify-center gap-2 text-[11px] font-semibold ${t.featured ? "text-primary-foreground/45" : "text-foreground/40"}`}>
                  <LockSimple size={12} weight="bold" /> Secure checkout by Stripe
                </p>
                <button
                  data-testid={`pricing-enquire-${t.id}`}
                  onClick={() => enquire(t.planNote)}
                  className={`mt-3 mx-auto block text-xs font-semibold border-b pb-0.5 transition-colors duration-300 ${
                    t.featured
                      ? "text-primary-foreground/60 border-primary-foreground/30 hover:text-accent hover:border-accent"
                      : "text-foreground/55 border-foreground/25 hover:text-accent hover:border-accent"
                  }`}
                >
                  Prefer to talk first? Request a free intro call
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-10 text-xs text-foreground/45 max-w-2xl leading-relaxed" data-testid="pricing-note">
            Not sure which fits? Book the Clarity Session — if your situation turns out to need the longer road,
            your session fee is credited toward membership if you continue. No one pays twice for the same answer.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
